import { useSyncExternalStore } from 'react';

import { FRAME_TIERS } from 'components/Streaks/AvatarFrame';
import { FrameTier, SeatFrameEntry, StreakSnapshot } from 'components/Streaks/model/types';

/**
 * The streak snapshot, outside Redux on purpose.
 *
 * PERF: react-redux runs every `useSelector` on every store dispatch, and a
 * busy table dispatches several times a second (game-client-render-perf
 * findings: more selectors measurably slowed device renders). Streak state
 * changes a handful of times a day, so it lives in this tiny store and
 * components read it through useSyncExternalStore: a table update costs the
 * streak surfaces nothing.
 */
export interface StreakStoreState {
  snapshot: StreakSnapshot | null;
  /** True once the first read finished (successfully or not). */
  loaded: boolean;
  /** The next account-panel open shows the card expanded (banner tap); consumed by the row. */
  expandPending: boolean;
  /** The buy freezes sheet is open. */
  buyOpen: boolean;
  /** The table whose streak chip is showing; the 100K promo chip steps aside there (EN-11163 AC 2). */
  chipTableId: string | null;
  /** The table whose streak burst is showing; its felt centre (board, pot, felt text, winner message) steps aside (EN-11386). */
  burstTableId: string | null;
  /** Other players' seat frames by GUID (EN-11164), newest version kept. */
  seatFrames: Record<string, SeatFrameEntry>;
}

const initial: StreakStoreState = {
  snapshot: null,
  loaded: false,
  expandPending: false,
  buyOpen: false,
  chipTableId: null,
  burstTableId: null,
  seatFrames: {}
};
let state: StreakStoreState = initial;
const listeners = new Set<() => void>();

function set(next: Partial<StreakStoreState>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

const asTier = (v: unknown): FrameTier | null =>
  typeof v === 'string' && Object.prototype.hasOwnProperty.call(FRAME_TIERS, v)
    ? (v as FrameTier)
    : null;

/** True when `incoming` is newer than `current` (version first, then read time). */
export function isNewer(incoming: StreakSnapshot, current: StreakSnapshot | null): boolean {
  if (!current) return true;
  if (incoming.version !== current.version) return incoming.version > current.version;
  return incoming.asOf > current.asOf;
}

export const streakStore = {
  getState: (): StreakStoreState => state,
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** Accept a snapshot from REST or the socket; an older one is ignored. */
  accept(snapshot: StreakSnapshot | null | undefined) {
    if (!snapshot) return;
    if (!isNewer(snapshot, state.snapshot)) {
      if (!state.loaded) set({ loaded: true });
      return;
    }
    set({ snapshot, loaded: true });
  },
  /**
   * Seat frames from the lookup or a push. A lookup reply older than a push
   * that beat it is ignored; an unknown tier reads as no frame.
   */
  acceptSeatFrames(entries: Record<string, { frame?: unknown; version?: unknown }>) {
    let next: Record<string, SeatFrameEntry> | null = null;
    for (const [guid, entry] of Object.entries(entries)) {
      const version = Number(entry?.version);
      if (!guid || !Number.isFinite(version)) continue;
      const frame = asTier(entry.frame);
      const current: SeatFrameEntry | undefined = (next ?? state.seatFrames)[guid];
      if (
        current &&
        (current.version > version || (current.version === version && current.frame === frame))
      )
        continue;
      next = { ...(next ?? state.seatFrames), [guid]: { frame, version } };
    }
    if (next) set({ seatFrames: next });
  },
  markLoaded() {
    if (!state.loaded) set({ loaded: true });
  },
  requestExpanded() {
    set({ expandPending: true });
  },
  consumeExpanded() {
    if (state.expandPending) set({ expandPending: false });
  },
  setChipTable(chipTableId: string | null) {
    if (state.chipTableId !== chipTableId) set({ chipTableId });
  },
  setBurstTable(burstTableId: string | null) {
    if (state.burstTableId !== burstTableId) set({ burstTableId });
  },
  setBuyOpen(buyOpen: boolean) {
    if (state.buyOpen !== buyOpen) set({ buyOpen });
  },
  /** Sign-out or a different player: forget everything. */
  reset() {
    state = initial;
    listeners.forEach((l) => l());
  }
};

export function useStreakStore<T>(select: (s: StreakStoreState) => T): T {
  return useSyncExternalStore(
    streakStore.subscribe,
    () => select(streakStore.getState()),
    () => select(initial)
  );
}

export const useStreakSnapshot = () => useStreakStore((s) => s.snapshot);
